import { fillDocumentDefaults } from "./default-fill";
import type { EmailDocument } from "./types";
import { EMAIL_DOCUMENT_SCHEMA_VERSION } from "./types";

export type IngestApiDocumentResult =
  | { ok: true; document: EmailDocument }
  | { ok: false; errors: string[] };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isRowSection(section: Record<string, unknown>): boolean {
  return Array.isArray(section.columns);
}

function isGroupSection(section: Record<string, unknown>): boolean {
  return Array.isArray(section.rows);
}

export function ingestEmailDocumentFromApi(input: unknown): IngestApiDocumentResult {
  if (!isPlainObject(input)) {
    return { ok: false, errors: ["Document must be an object."] };
  }

  if ("styles" in input && !("settings" in input)) {
    return { ok: false, errors: ["Unsupported document format (expected settings, not styles)."] };
  }

  if (!isPlainObject(input.settings)) {
    return { ok: false, errors: ["Document settings must be an object."] };
  }

  if (!Array.isArray(input.sections)) {
    return { ok: false, errors: ["Document sections must be an array."] };
  }

  const schemaVersion = input.schemaVersion;
  if (
    typeof schemaVersion !== "number" ||
    !Number.isInteger(schemaVersion) ||
    schemaVersion !== EMAIL_DOCUMENT_SCHEMA_VERSION
  ) {
    const schemaVersionLabel =
      typeof schemaVersion === "string" || typeof schemaVersion === "number"
        ? String(schemaVersion)
        : "invalid";
    return {
      ok: false,
      errors: [`Unsupported schemaVersion ${schemaVersionLabel}.`],
    };
  }

  for (const section of input.sections) {
    if (!isPlainObject(section)) {
      return { ok: false, errors: ["Each section must be an object."] };
    }

    if ("role" in section) {
      return { ok: false, errors: ["Section role is not supported."] };
    }

    if (!isRowSection(section) && !isGroupSection(section)) {
      return { ok: false, errors: ["Section must be a row or group."] };
    }
  }

  return {
    ok: true,
    document: fillDocumentDefaults(input as EmailDocument),
  };
}
